import { useAtom } from 'jotai';
import React, { FunctionComponent } from 'react';

import {
  Stack,
  Button,
  useTheme,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

import {
  isInitializingAtom,
  locationsAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  sitesAtom,
  selectedSitesAtom,
  currentKQLQueryAtom,
  currentSortQueryAtom,
  hitHighlightingEnabledAtom,
  useRefinerOverridesAtom,
  refinersExpandedAtom
} from '../utils/AtomConfig';
import { ISite } from '../models/ISite';
import Constants from '../utils/Constants';
import { useQuery } from 'react-query';
import { MdiService } from '../utils/MdiService';

interface IQuickFilterProps {
  insideNavBar?: boolean;
}

export const QuickFilter: FunctionComponent<IQuickFilterProps> = (
  props: IQuickFilterProps
) => {
  const mdiService = MdiService.Instance;
  const theme = useTheme();
  const [locations] = useAtom(locationsAtom);
  const [selectedLocationId] = useAtom(selectedLocationIdAtom);
  const [selectedSites, setSelectedSites] = useAtom(selectedSitesAtom);
  const [, setSites] = useAtom(sitesAtom);
  const [, setIsInitializing] = useAtom(isInitializingAtom);
  const [, setSelectedRefinerItems] = useAtom(selectedRefinerItemsAtom);

  const [refinersExpanded, setRefinersExpanded] = useAtom(
    refinersExpandedAtom
  );

  const sitesQuery = useQuery(
    [Constants.QUERY_KEYS.SITES, selectedLocationId],
    () => {
      const selectedLocation = locations.find(
        (location) => location.id === selectedLocationId
      );

      return mdiService.GetSites(selectedLocation?.title || '');
    },
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled: selectedLocationId !== Constants.DEFAULT_ID,
      onSuccess: (data) => {
        setSites(data); 
        updateSelectedSite(data[0]);
      },
    }
  );

  const [currentKQLQuery, setCurrentKQLQuery] = useAtom(
    currentKQLQueryAtom
  );

  const [currentSortQuery, setCurrentSortQuery] = useAtom(
    currentSortQueryAtom
  );

  const [hitHighlightingEnabled, setHitHighlightingEnabled] = useAtom(
    hitHighlightingEnabledAtom
  );

  const [useRefinerOverrides, setUseRefinerOverrides] = useAtom(
    useRefinerOverridesAtom
  );

  function isSiteSelected(site: string) {
    return selectedSites.indexOf(site) !== Constants.DEFAULT_ID;
  }

  function updateSelectedSite(site: ISite) {
    if (site.Title === "Home") {
      setRefinersExpanded(false);
    } else {
      setRefinersExpanded(true);
    }

    setSelectedRefinerItems([]);
    setIsInitializing(true);
    setSelectedSites([site.Title]);
    setCurrentKQLQuery(site.KQLQuery);
    setCurrentSortQuery(site.Sort);

    if (site.UseRefinerOverrides !== null) {
      setUseRefinerOverrides(site.UseRefinerOverrides);
    } else {
      setUseRefinerOverrides(false);
    }

    if (site.HitHighliting !== null) {
      setHitHighlightingEnabled(site.HitHighliting);
    } else {
      setHitHighlightingEnabled(false);
    }
  }

  const isLocationSelected = selectedLocationId !== Constants.DEFAULT_ID;
  const mainStyle : any = {
    position: 'fixed',
    top: 64,
    bottom: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    width: '240px',
    overflowY: 'auto',
    boxShadow: '0px 0px 10px rgb(0 0 0 / 30%)',
    zIndex: 1,
    overflowX: 'hidden',
    '& button': {
      borderRadius: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      display: 'block',
      '&.active, &.active:hover': {
        backgroundColor: 'rgba(0,0,0,0.15)',
      },
      '&:not(.active):hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    display: {
      xs: 'none',
      md: 'flex',
    },
  }

  const navBarStyle : any = {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    overflowY: 'auto',
    boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    marginTop: '20px',
    zIndex: 1,
    minHeight: '250px',
    overflowX: 'hidden',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    '& button': {
      borderRadius: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      display: 'block',
      '&.active, &.active:hover': {
        backgroundColor: 'rgba(0,0,0,0.15)',
      },
      '&:not(.active):hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    display: {
      xs: 'flex',
      md: 'none',
    },
  }

  const headerNavBarStyle : any =  {
    marginLeft: '16px',
    marginTop: '16px'
  }

  const headerMainStyle : any =  {
    display: 'none',
  }

  return (
    <Stack
      direction='column'
      sx={props.insideNavBar ? navBarStyle : mainStyle}
    >
      <Typography
        sx={props.insideNavBar ? headerNavBarStyle : headerMainStyle}
      >
        Site
      </Typography>
      {/* Show loading spinner */}
      {sitesQuery.isLoading && (
        <Box sx={{ marginTop: 3, textAlign: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {/* Show message if location is not selected */}
      {!isLocationSelected && (
        <Typography align='center' color='black' marginTop={2}>
          Please select location first
        </Typography>
      )}

      {sitesQuery.data?.map((site, index) => (
        <Button
          key={site.Id}
          className={isSiteSelected(site.Title) ? 'active' : ''}
          onClick={() => updateSelectedSite(site)}
        >
          {site.Title}
        </Button>
      ))}
    </Stack>
  );
};
